import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  api,
  currentRefreshToken,
  ensureAccessToken,
  hasStoredSession,
  setTokens,
} from "../lib/api";
import type { UserPrivate } from "../lib/types";

interface AuthValue {
  user: UserPrivate | null;
  /** True until the stored session has been checked, so guards do not flash the login page. */
  booting: boolean;
  login: (identifier: string, password: string) => Promise<UserPrivate>;
  loginGoogle: (credential: string) => Promise<UserPrivate>;
  exchangeOAuthCode: (code: string) => Promise<UserPrivate>;
  getTelegramLoginUrl: () => Promise<string>;
  register: (input: { username: string; email: string; password: string }) => Promise<UserPrivate>;
  logout: (allSessions?: boolean) => Promise<void>;
  reload: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserPrivate | null>(null);
  const [booting, setBooting] = useState<boolean>(hasStoredSession());

  useEffect(() => {
    let cancelled = false;
    if (!hasStoredSession()) {
      setBooting(false);
      return;
    }
    void (async () => {
      try {
        if (await ensureAccessToken()) {
          const me = await api.auth.me();
          if (!cancelled) setUser(me);
        } else if (!cancelled) {
          setTokens(null);
        }
      } catch {
        if (!cancelled) setTokens(null);
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (identifier: string, password: string) => {
    const tokens = await api.auth.login({ identifier, password });
    setTokens(tokens);
    if (tokens.user) {
      setUser(tokens.user);
      return tokens.user;
    }
    const me = await api.auth.me();
    setUser(me);
    return me;
  }, []);

  const loginGoogle = useCallback(async (credential: string) => {
    const tokens = await api.auth.google({ credential });
    setTokens(tokens);
    if (tokens.user) {
      setUser(tokens.user);
      return tokens.user;
    }
    const me = await api.auth.me();
    setUser(me);
    return me;
  }, []);

  const exchangeOAuthCode = useCallback(async (code: string) => {
    const tokens = await api.auth.exchange({ code });
    setTokens(tokens);
    if (tokens.user) {
      setUser(tokens.user);
      return tokens.user;
    }
    const me = await api.auth.me();
    setUser(me);
    return me;
  }, []);

  const getTelegramLoginUrl = useCallback(async () => {
    const resp = await api.auth.telegramLogin();
    return resp.url;
  }, []);

  const register = useCallback(
    async (input: { username: string; email: string; password: string }) => {
      await api.auth.register(input);
      return login(input.username, input.password);
    },
    [login],
  );

  const logout = useCallback(async (allSessions = false) => {
    try {
      await api.auth.logout({ refresh_token: currentRefreshToken(), all_sessions: allSessions });
    } catch {
      // Revoking server-side is best effort; the local session is dropped either way.
    }
    setTokens(null);
    setUser(null);
  }, []);

  const reload = useCallback(async () => {
    if (!hasStoredSession()) return;
    try {
      setUser(await api.auth.me());
    } catch {
      /* leave the current value in place */
    }
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      booting,
      login,
      loginGoogle,
      exchangeOAuthCode,
      getTelegramLoginUrl,
      register,
      logout,
      reload,
    }),
    [
      user,
      booting,
      login,
      loginGoogle,
      exchangeOAuthCode,
      getTelegramLoginUrl,
      register,
      logout,
      reload,
    ],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
